import React from 'react';
import Link from 'next/link';
import { AppImage, Typography, typographyCva } from '@workspace/ui/common';
import { cn } from '@workspace/lib';
import type { ProductSlideshowSlide } from '../product-slideshow.types';
import { CardLinkButton } from './CardLinkButton';
import { sanityImageSrc } from '@/sanity/lib/image';

export function ProductSlide({
  productRef,
  hyperlinkLabel,
  productThumbnail,
  productDescription,
  className,
}: ProductSlideshowSlide) {
  const href =
    typeof productRef?.productURL === 'string' && productRef.productURL.trim() ? productRef.productURL : undefined;

  const productName = typeof productRef?.name === 'string' && productRef.name.trim() ? productRef.name : '';
  const descriptionText = typeof productDescription === 'string' ? productDescription : '';
  const thumbnailUrl =
    typeof productThumbnail?.url === 'string' && productThumbnail.url.trim() ? productThumbnail.url : '';
  const thumbnailAlt =
    typeof productThumbnail?.bynderImage?.alt === 'string' && productThumbnail.bynderImage.alt.trim()
      ? productThumbnail.bynderImage.alt
      : typeof productThumbnail?.alt === 'string' && productThumbnail.alt.trim()
        ? productThumbnail.alt
        : productName || 'Product Image';
  const linkLabel = typeof hyperlinkLabel === 'string' && hyperlinkLabel.trim() ? hyperlinkLabel.trim() : '';

  const hasContent = productName || thumbnailUrl || descriptionText || linkLabel;
  if (!hasContent) return null;

  return (
    <CardWrapper href={href}>
      <div className={cn('group flex flex-col bg-white overflow-hidden max-w-96 w-full rounded-md', className)}>
        {/* Image */}
        <div className="relative overflow-hidden bg-secondary-100 aspect-[4/3] w-full rounded-t-md border border-b-0 border-secondary-100 md:h-[18.125rem] h-[15.3125rem]">
          {thumbnailUrl ? (
            <AppImage
              src={sanityImageSrc(productThumbnail, {
                width: 382,
                height: 289,
              })}
              alt={thumbnailAlt}
              fill
              className="object-cover transition-transform duration-500 ease-out group-hover:scale-105 rounded-t-md"
            />
          ) : (
            <div className="size-full bg-secondary-100 flex items-center justify-center">
              <Typography variant="bodySm" className="text-gray-500">
                No product selected
              </Typography>
            </div>
          )}
        </div>

        <div className="md:min-h-57 min-h-46 p-sm md:py-md flex flex-col border border-t-gray-100 border-secondary-100 !rounded-b-md">
          {!!productRef && (
            <>
              {/* Heading */}
              {productName && (
                <Typography
                  variant="h2"
                  className={cn('line-clamp-2 w-full text-secondary-400', typographyCva({ variant: 'h6' }))}
                >
                  {productName}
                </Typography>
              )}
              {/* Description */}
              {descriptionText && (
                <Typography title={descriptionText} variant="bodySm" className="line-clamp-3 mt-xs font-openSans">
                  {descriptionText}
                </Typography>
              )}

              {/* Read more btn */}
              {linkLabel && (
                <div className="mt-auto flex items-center justify-end">
                  <CardLinkButton>{linkLabel}</CardLinkButton>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </CardWrapper>
  );
}

const CardWrapper = ({ children, href }: { children: React.ReactNode; href?: string }) => {
  if (href) return <Link href={href}>{children}</Link>;
  return <>{children}</>;
};
